import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { SubPageHeader } from "@/components/SubPageHeader";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Chip } from "@/components/Chip";
import { Plus, X, Check, X as XIcon, Wrench } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, todayISO } from "@/lib/date";

export default function Regularization() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [regs, setRegs] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", date: todayISO(), check_in_time: "10:45", check_out_time: "19:45", reason: "" });

  const load = async () => {
    try {
      const [r, u] = await Promise.all([api.get("/regularizations"), api.get("/users")]);
      setRegs(r.data.sort((a, b) => b.applied_at.localeCompare(a.applied_at)));
      setUsers(u.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/regularizations", { ...form, user_id: isHR ? (form.user_id || user.user_id) : undefined });
      toast.success("Submitted");
      setOpen(false);
      setForm({ user_id: "", date: todayISO(), check_in_time: "10:45", check_out_time: "19:45", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/regularizations/${id}`, { status }); toast.success(status); load(); }
    catch { toast.error("Failed"); }
  };

  const isAdmin = user?.role === "admin";
  const isHR = user?.role === "admin" || user?.role === "hr_manager";

  return (
    <div className="min-h-[100svh]" style={{ background: "#0A0D14", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="reg-page">
      <SubPageHeader title="Regularization" right={
        <button onClick={() => setOpen(true)} data-testid="submit-reg-button" className="h-9 px-3 rounded-lg flex items-center gap-1 text-xs font-bold" style={{ background: "#F7C94F", color: "#0A0D14" }}>
          <Plus size={14} /> New
        </button>
      } />
      <div className="px-4 py-4 space-y-3">
        <div className="rounded-xl px-4 py-3 text-xs flex items-center gap-2" style={{ background: "rgba(247,201,79,0.1)", border: "1px solid rgba(247,201,79,0.3)", color: "#F7C94F" }}>
          <Wrench size={14} />
          Window: {settings?.regularization_window || 3} days · Grace: {settings?.grace_period || 15} min
        </div>

        {regs.length === 0 && <div className="text-center text-sm py-8" style={{ color: "#7A85A3" }}>No requests</div>}
        {regs.map((r) => (
          <div key={r.id} className="rounded-2xl p-4" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`reg-${r.id}`}>
            <div className="flex items-start gap-2 mb-2">
              <div className="flex-1 min-w-0">
                {isHR && <div className="font-bold text-sm truncate" style={{ color: "#E8ECFF" }}>{r.user_name}</div>}
                <div className="text-xs" style={{ color: "#7A85A3" }}>{fmtDate(r.date, "EEE, d MMM yyyy")}</div>
              </div>
              <Chip color={r.status === "approved" ? "green" : r.status === "rejected" ? "red" : "amber"}>{r.status}</Chip>
            </div>
            <div className="font-mono text-sm mb-1" style={{ color: "#E8ECFF" }}>{r.check_in_time} → {r.check_out_time}</div>
            {r.reason && <div className="text-xs" style={{ color: "#7A85A3" }}>{r.reason}</div>}
            {isHR && r.status === "pending" && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => setStatus(r.id, "approved")} data-testid={`reg-approve-${r.id}`} className="flex-1 h-9 rounded-lg flex items-center justify-center gap-1 text-xs font-bold" style={{ background: "rgba(34,211,165,0.15)", color: "#22D3A5", border: "1px solid rgba(34,211,165,0.3)" }}>
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => setStatus(r.id, "rejected")} data-testid={`reg-reject-${r.id}`} className="flex-1 h-9 rounded-lg flex items-center justify-center gap-1 text-xs font-bold" style={{ background: "rgba(247,90,90,0.1)", color: "#F75A5A", border: "1px solid rgba(247,90,90,0.3)" }}>
                  <XIcon size={14} /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent style={{ background: "#111520", borderColor: "#1E2640" }}>
          <div className="mx-auto w-12 h-1.5 rounded-full mt-2 mb-3" style={{ background: "#1E2640" }} />
          <div className="px-6 pb-8 space-y-3" data-testid="reg-form">
            <div className="flex items-center justify-between mb-2">
              <div className="font-syne text-xl font-bold tracking-tight" style={{ color: "#E8ECFF" }}>Submit Regularization</div>
              <button onClick={() => setOpen(false)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}><X size={16} /></button>
            </div>
            {isHR && (
              <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="reg-user"
                className="w-full h-11 px-3 rounded-xl text-sm" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }}>
                <option value="">Self ({user.name})</option>
                {users.filter((u) => u.user_id !== user.user_id).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            )}
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reg-date"
              className="w-full h-11 px-3 rounded-xl" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            <div className="grid grid-cols-2 gap-2">
              <input type="time" value={form.check_in_time} onChange={(e) => setForm({ ...form, check_in_time: e.target.value })} data-testid="reg-in"
                className="h-11 px-3 rounded-xl" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
              <input type="time" value={form.check_out_time} onChange={(e) => setForm({ ...form, check_out_time: e.target.value })} data-testid="reg-out"
                className="h-11 px-3 rounded-xl" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            </div>
            <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason" rows={3} data-testid="reg-reason"
              className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            <button onClick={submit} data-testid="reg-submit" className="w-full h-12 rounded-xl font-bold" style={{ background: "#F7C94F", color: "#0A0D14" }}>Submit</button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
